import { FlipperKey } from '~/store/flippers/types'
import { Dispatch } from '~/store/types'
import { BLOCKCHAIN_ADDRESS_ACTIONS, FLIPPER_RESPONSE_MAP } from './const'
import { StateFlippers } from './reducer'
import { ActionTypes, BlockchainAddress, WalletConnectSignature } from './types'
import { ENDPOINTS, get, post } from '~/api'
import { keysToCamel, objectToQueryParams } from '~/helpers/helpers'
import { FinancialInstitution } from '~/types'

export const setBlockchainAddressList =
  (blockchainAddressList: BlockchainAddress[]) => (dispatch: Dispatch<ActionTypes>) => {
    dispatch({
      type: BLOCKCHAIN_ADDRESS_ACTIONS.SET_BLOCKCHAIN_ADDRESS_LIST,
      blockchainAddressList,
    })
  }

const setFinancialInstitutionList =
  (fiList: FinancialInstitution[]) => (dispatch: Dispatch<ActionTypes>) => {
    dispatch({
      type: BLOCKCHAIN_ADDRESS_ACTIONS.SET_FI_LIST,
      fiList,
    })
  }

const setMaxAddressAmount = (maxAddressAmount: number) => (dispatch: Dispatch<ActionTypes>) => {
  dispatch({
    type: BLOCKCHAIN_ADDRESS_ACTIONS.SET_MAX_ADDRESS_AMOUNT,
    maxAddressAmount,
  })
}

const setAddressQuota = (addressQuota: number) => (dispatch: Dispatch<ActionTypes>) => {
  dispatch({
    type: BLOCKCHAIN_ADDRESS_ACTIONS.SET_ADDRESS_QUOTA,
    addressQuota,
  })
}

export const fetchBlockchainAddresses =
  (accountId: number, successCallback?: () => void) => async (dispatch: Dispatch<ActionTypes>) => {
    const subRoute = `/${accountId}`

    const resp = await get(ENDPOINTS.API_V3_STABLECOIN_ADDRESSES, subRoute)
    if (resp.data.address_list) {
      const respList = resp.data.address_list as []
      const addressList: BlockchainAddress[] = respList.map((blockchainAddress) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        keysToCamel(blockchainAddress)
      )
      dispatch(setBlockchainAddressList(addressList))
    }

    if (resp.data.fi_list) {
      dispatch(setFinancialInstitutionList(resp.data.fi_list as FinancialInstitution[]))
    }

    if (resp.data.max_address_amount && !Number.isNaN(resp.data.max_address_amount)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      dispatch(setMaxAddressAmount(resp.data.max_address_amount))
    }

    if (
      (resp.data.address_quota === 0 || resp.data.address_quota) &&
      !Number.isNaN(resp.data.address_quota)
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      dispatch(setAddressQuota(resp.data.address_quota))
    }

    if (successCallback) {
      successCallback()
    }
  }

export const createBlockchainAddress =
  (
    isWhitelistingImprovementEnabled: boolean,
    addressCustodialType: string,
    blockchain: string,
    nickname: string,
    provider: string,
    address: string,
    accountId?: number,
    successCallback?: (address: BlockchainAddress) => void,
    errorCallback?: (errorMessage: string) => void
  ) =>
  (dispatch: Dispatch<ActionTypes>) => {
    post(
      ENDPOINTS.API_V3_STABLECOIN_ADDRESSES,
      {
        address_custodial_type: addressCustodialType,
        blockchain,
        nickname,
        provider,
        address,
      },
      {},
      `/${accountId}`
    )
      .then((resp) => {
        if (successCallback) {
          if (isWhitelistingImprovementEnabled && accountId) {
            const fetchBlockchainAddressesSuccessCallback = () => {
              const blockchainAddress: BlockchainAddress[] = resp.data.filter(
                (obj: BlockchainAddress) => obj.address === address && obj.blockchain === blockchain
              )
              successCallback(blockchainAddress[0])
            }
            dispatch(fetchBlockchainAddresses(accountId, fetchBlockchainAddressesSuccessCallback))
          } else {
            const blockchainAddress: BlockchainAddress = keysToCamel(resp)
            successCallback(blockchainAddress)
          }
        }
      })
      .catch((error: { response: { data: { error: string } } }) => {
        if (errorCallback) {
          errorCallback(error?.response?.data?.error)
        }
      })
  }

export const updateBlockchainAddress =
  (blockchainAddress: BlockchainAddress) => (dispatch: Dispatch<ActionTypes>) => {
    dispatch({
      type: BLOCKCHAIN_ADDRESS_ACTIONS.UPDATE_BLOCKCHAIN_ADDRESS,
      blockchainAddress,
    })
  }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setFlippers =
  (keys: FlipperKey[], flipperResponse: any) => (dispatch: Dispatch<ActionTypes>) => {
    const flippers = new Map<keyof StateFlippers, boolean>()

    if (keys && flipperResponse) {
      keys.forEach((key) => {
        const prop = FLIPPER_RESPONSE_MAP[key]
        const enabled = flipperResponse[key]

        if (prop && enabled !== undefined) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          flippers.set(prop, enabled)
        }
      })

      if (flippers.size > 0) {
        dispatch({
          type: BLOCKCHAIN_ADDRESS_ACTIONS.SET_FLIPPERS,
          flippers,
        })
      }
    }
  }

export const createWhitelistAddress =
  (
    params: {
      accountId: number
      addressType: string
      blockchain: string
      financialInstitutionId?: number
      nickname: string
      address: string
      destinationTag?: string
      custodialName: string
      isFirstParty: boolean
      beneficiaryName?: string
    },
    successCallback?: () => void,
    errorCallback?: (errorMessage: string) => void
  ) =>
  async () => {
    const {
      accountId,
      addressType,
      blockchain,
      financialInstitutionId,
      nickname,
      address,
      destinationTag,
      custodialName,
      isFirstParty,
      beneficiaryName,
    } = params

    try {
      await post(
        ENDPOINTS.API_V3_STABLECOIN_ADDRESSES,
        {
          address_custodial_type: addressType,
          blockchain,
          custodial: financialInstitutionId,
          nickname,
          address,
          destination_tag: destinationTag,
          custodial_name: custodialName,
          is_first_party: isFirstParty,
          beneficiary_name: beneficiaryName?.trim(),
        },
        {},
        `/${accountId}`
      )
      if (successCallback) successCallback()
    } catch (error) {
      const err = error as { response?: { data?: { error?: string } } }
      if (errorCallback) {
        errorCallback(err?.response?.data?.error || 'An unexpected error occurred')
      }
    }
  }

// submitVerifiedAddress (migrated from app & biz store action) is to submit the signed message (via Metamask or Wallet Connect) to backend for address verification
export const submitVerifiedAddress =
  (
    address: BlockchainAddress,
    msg: string,
    sign: WalletConnectSignature | string,
    successCallback: () => void,
    errorCallback: (errorMessage: string) => void,
    accountId?: number
  ) =>
  async () => {
    const body = {
      msg,
      sign: '',
    }
    if (typeof sign === 'string') {
      body.sign = sign.slice(2)
    } else if (sign && typeof sign === 'object' && 'signature' in sign) {
      body.sign = sign.signature.slice(2)
    } else {
      throw new Error('Invalid sign parameter')
    }
    const queryParams = objectToQueryParams({ blockchain: address.blockchain })
    const addressId = address.id
    // TODO(duy): move HTTP requests to api layer
    const subRoute = `/${accountId}/${addressId}/approve_signed_token_address?${queryParams}`
    const resp: { data: Record<string, unknown> } = await post(
      ENDPOINTS.API_V3_STABLECOIN_ADDRESSES,
      body,
      {},
      subRoute
    )

    if (resp.data) {
      if (successCallback) {
        successCallback()
      }
    } else {
      errorCallback('Failed to verify address')
    }
  }
